import Spinner from '../Spinner/Spinner';
import { Smile } from 'lucide-react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { protectedUrl } from '../../../service/request';
import { clsx } from '../../utils/clsx';
import { Avatar } from '../Avatar/Avatar';
import Button from '../Button/Button';
import { LinkPreviewList } from '../LinkPreviewList';
import Popover from '../Popover/Popover';
import s from './TaggableComposer.module.css';
import TaggableComposerEmojiPicker from './TaggableComposerEmojiPicker';
import {
  buildMentionsByType,
  extractUrls,
  mapInitialPreviews,
  normalizeUrl,
  sanitizeComposerHtml,
  siteNameFromUrl,
  stripHtmlToPlain,
  URL_REGEX,
} from './utils';

const DEFAULT_MENTION_TYPES = ['user', 'grave'];
const LINK_PREVIEW_DEBOUNCE_MS = 600;
const MENTION_QUERY_REGEX = /(?:^|[\s\u00A0])@([\p{L}\p{N}._-]+(?:[\s\u00A0]+[\p{L}\p{N}._-]+)*)$/u;

const mentionClassForType = (type) => {
  if (type === 'grave') return clsx('spark-mention', 'spark-mention--grave', s.mention, s.mentionGrave);
  return clsx('spark-mention', s.mention, s.mentionUser);
};

const escapeHtml = (text) =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const textToHtml = (text) =>
  escapeHtml(text || '')
    .split(/\n/g)
    .map((line) => (line.length ? line : '<br>'))
    .join('<br>');

const insertHtmlAtCursor = (container, html) => {
  if (!container) return;
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    container.insertAdjacentHTML('beforeend', html);
    return;
  }
  const range = selection.getRangeAt(0);
  range.deleteContents();
  const fragment = range.createContextualFragment(html);
  const lastNode = fragment.lastChild;
  range.insertNode(fragment);
  if (lastNode) {
    const newRange = range.cloneRange();
    newRange.setStartAfter(lastNode);
    newRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(newRange);
  }
};

const findPreviousTextNode = (node) => {
  let current = node;
  while (current && current !== current.ownerDocument) {
    if (current.previousSibling) {
      current = current.previousSibling;
      while (current && current.lastChild) {
        current = current.lastChild;
      }
      if (current && current.nodeType === Node.TEXT_NODE) return current;
    } else {
      current = current.parentNode;
    }
  }
  return null;
};

const collectMentionsFromContainer = (container) => {
  if (!container) return [];
  const nodes = container.querySelectorAll('[data-mention-id]');
  return Array.from(nodes).map((node) => {
    const nameFromText = (node.textContent || '').replace(/^@/, '');
    return {
      id: node.getAttribute('data-mention-id') || '',
      type: node.getAttribute('data-mention-type') || 'user',
      name: node.getAttribute('data-mention-name') || nameFromText,
      slug: node.getAttribute('data-mention-slug') || '',
      href: node.getAttribute('data-mention-href') || node.getAttribute('href') || '',
    };
  });
};

const TaggableComposer = forwardRef(
  (
    {
      value = '',
      initialLinkPreviews,
      placeholder,
      mentionEnabled = true,
      mentionTypes = DEFAULT_MENTION_TYPES,
      mentionLimit = 6,
      fetchMentionables,
      linkPreviewEnabled = true,
      fetchLinkPreview,
      maxLength,
      showCharCount = true,
      emojiButtonEnabled = true,
      ariaLabel,
      ariaLabelledBy,
      className,
      style,
      onChange,
      onEmojiClick,
    },
    ref,
  ) => {
    const intl = useIntl();
    const editorRef = useRef(null);
    const editorContainerRef = useRef(null);
    const mentionRangeRef = useRef(null);
    const emojiSelectionRef = useRef(null);
    const emojiSuppressCloseRef = useRef(false);
    const mentionRequestRef = useRef(0);
    const initialSyncRef = useRef({ valueKey: null, previewsKey: null });
    const lastPayloadSignatureRef = useRef(null);
    const lastValidStateRef = useRef({
      html: sanitizeComposerHtml(value),
      plainText: stripHtmlToPlain(value),
    });

    const mentionTypesList = useMemo(() => {
      if (Array.isArray(mentionTypes) && mentionTypes.length > 0) return mentionTypes;
      return DEFAULT_MENTION_TYPES;
    }, [mentionTypes]);

    const [isFocused, setIsFocused] = useState(false);
    const [html, setHtml] = useState(() => sanitizeComposerHtml(value));
    const [plainText, setPlainText] = useState(() => stripHtmlToPlain(value));
    const [mentionActive, setMentionActive] = useState(false);
    const [mentionQuery, setMentionQuery] = useState('');
    const [mentionSuggestions, setMentionSuggestions] = useState([]);
    const [mentionHighlightedIndex, setMentionHighlightedIndex] = useState(0);
    const [mentionLoading, setMentionLoading] = useState(false);
    const [emojiOpen, setEmojiOpen] = useState(false);
    const [previewMap, setPreviewMap] = useState(() => mapInitialPreviews(initialLinkPreviews));
    const previewTimersRef = useRef(new Map());
    const mentionAnchorRef = useRef(null);
    const linkifyEditorContent = useCallback(() => {
      if (typeof window === 'undefined') return;
      const container = editorRef.current;
      if (!container) return;

      const walker = window.document.createTreeWalker(
        container,
        NodeFilter.SHOW_TEXT,
        null,
        false,
      );
      const replacements = [];

      while (walker.nextNode()) {
        const textNode = walker.currentNode;
        if (!textNode || !textNode.nodeValue) continue;
        const parentElement = textNode.parentElement;
        if (parentElement && parentElement.closest('a')) continue;
        const text = textNode.nodeValue;
        const regex = new RegExp(URL_REGEX);
        let match;
        let lastIndex = 0;
        const segments = [];

        while ((match = regex.exec(text))) {
          const raw = match[0];
          const normalized = normalizeUrl(raw);
          if (!normalized) continue;
          const start = match.index;
          if (start > lastIndex) {
            segments.push({ type: 'text', value: text.slice(lastIndex, start) });
          }
          segments.push({ type: 'link', value: raw, url: normalized });
          lastIndex = start + raw.length;
        }

        if (segments.length === 0) continue;
        if (lastIndex < text.length) {
          segments.push({ type: 'text', value: text.slice(lastIndex) });
        }

        replacements.push({ node: textNode, segments });
      }

      replacements.forEach(({ node, segments }) => {
        const fragment = window.document.createDocumentFragment();
        segments.forEach((segment) => {
          if (segment.type === 'link') {
            const anchor = window.document.createElement('a');
            anchor.textContent = segment.value;
            anchor.href = segment.url;
            anchor.rel = 'noopener noreferrer nofollow';
            anchor.target = '_blank';
            anchor.className = clsx(s.autoLink, 'spark-auto-link');
            fragment.appendChild(anchor);
          } else {
            fragment.appendChild(window.document.createTextNode(segment.value));
          }
        });

        if (node.parentNode) {
          node.parentNode.replaceChild(fragment, node);
        }
      });
    }, []);
    const focusEditor = useCallback(() => {
      const node = editorRef.current;
      if (!node) return;
      if (typeof node.focus === 'function') node.focus();
      if (typeof window === 'undefined') return;
      const selection = window.getSelection();
      if (!selection) return;
      if (selection.rangeCount === 0) {
        const range = window.document.createRange();
        range.selectNodeContents(node);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }, []);

    const saveCurrentSelection = useCallback(() => {
      if (typeof window === 'undefined') return;
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      const container = editorRef.current;
      if (!container || !container.contains(range.startContainer)) return;
      emojiSelectionRef.current = range.cloneRange();
    }, []);

    const handleEmojiButtonClick = useCallback(
      (event) => {
        if (typeof onEmojiClick === 'function') {
          onEmojiClick(event);
        }
        emojiSuppressCloseRef.current = false;
        saveCurrentSelection();
      },
      [onEmojiClick, saveCurrentSelection],
    );

    const resolveMentionContainer = useCallback(() => {
      if (typeof document === 'undefined') return null;
      // Use a stable, shared portal root so unmounting the editor container
      // doesn't race with popover teardown (which can trigger removeChild errors).
      const hostId = 'ds-portal-root';
      let host = document.getElementById(hostId);
      if (!host) {
        host = document.createElement('div');
        host.id = hostId;
        host.style.position = 'relative';
        document.body.appendChild(host);
      }
      return host;
    }, []);
    const [mentionAnchorStyle, setMentionAnchorStyle] = useState(null);
    const lastMentionQueryRef = useRef('');

    useEffect(() => {
      if (!emojiButtonEnabled) {
        setEmojiOpen(false);
      }
    }, [emojiButtonEnabled]);

    useEffect(() => {
      return () => {
        mentionRangeRef.current = null;
        emojiSelectionRef.current = null;
        emojiSuppressCloseRef.current = false;
        mentionRequestRef.current += 1;
        lastMentionQueryRef.current = '';

        const editorNode = editorRef.current;
        const editorContainerNode = editorContainerRef.current;
        const activeElement = typeof document !== 'undefined' ? document.activeElement : null;
        if (
          activeElement &&
          editorContainerNode &&
          activeElement instanceof Node &&
          editorContainerNode.contains(activeElement)
        ) {
          activeElement.blur?.();
        }

        if (typeof window !== 'undefined') {
          const selection = window.getSelection();
          if (selection && selection.rangeCount > 0 && editorNode) {
            const range = selection.getRangeAt(0);
            const touchesEditor =
              editorNode.contains(range.startContainer) || editorNode.contains(range.endContainer);
            if (touchesEditor) {
              selection.removeAllRanges();
            }
          }
        }
      };
    }, []);

    const resetMentionState = useCallback(() => {
      setMentionActive(false);
      setMentionQuery('');
      setMentionSuggestions([]);
      setMentionAnchorStyle(null);
      lastMentionQueryRef.current = '';
      setMentionLoading(false);
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          focusEditor();
        },
        blur: () => {
          editorRef.current?.blur();
        },
      }),
      [focusEditor],
    );

    const syncInitialState = useCallback(() => {
      const sanitizedValue = sanitizeComposerHtml(value);
      const valueKey = `${mentionEnabled ? '1' : '0'}::${sanitizedValue}`;
      const normalizedInitialPreviews = Array.isArray(initialLinkPreviews)
        ? initialLinkPreviews.map((preview) => ({
            url: normalizeUrl(preview?.url) || '',
            siteName: preview?.siteName || '',
            title: preview?.title || '',
            description: preview?.description || '',
            imageUrl: preview?.imageUrl || '',
          }))
        : [];
      const previewsKey = JSON.stringify(normalizedInitialPreviews);

      if (
        initialSyncRef.current.valueKey === valueKey &&
        initialSyncRef.current.previewsKey === previewsKey
      ) {
        return;
      }

      initialSyncRef.current = { valueKey, previewsKey };
      let nextHtml = sanitizedValue;

      if (editorRef.current) {
        editorRef.current.innerHTML = sanitizedValue;
        linkifyEditorContent();
        const linkified = editorRef.current.innerHTML;
        const sanitizedLinkified = sanitizeComposerHtml(linkified);
        if (editorRef.current.innerHTML !== sanitizedLinkified) {
          editorRef.current.innerHTML = sanitizedLinkified;
        }
        nextHtml = sanitizedLinkified;
      }

      const nextPlain = stripHtmlToPlain(nextHtml);
      setHtml(nextHtml);
      setPlainText(nextPlain);
      lastValidStateRef.current = {
        html: nextHtml,
        plainText: nextPlain,
      };
      setPreviewMap(mapInitialPreviews(initialLinkPreviews));
    }, [initialLinkPreviews, linkifyEditorContent, mentionEnabled, value]);

    useEffect(() => {
      syncInitialState();
    }, [syncInitialState]);

    const urls = useMemo(() => extractUrls(plainText), [plainText]);

    const positionMentionAnchor = useCallback((range) => {
      const editorNode = editorRef.current;
      const containerNode = editorContainerRef.current || editorNode;
      if (!editorNode || !containerNode || !range) return;
      const rect = range.getClientRects()?.[0] || range.getBoundingClientRect();
      const containerRect = containerNode.getBoundingClientRect();
      if (!rect || !containerRect) return;
      const top = rect.bottom - containerRect.top + editorNode.scrollTop;
      const left = rect.left - containerRect.left + editorNode.scrollLeft;
      setMentionAnchorStyle({ top, left });
    }, []);

    const updateMentionContext = useCallback(() => {
      if (!mentionEnabled) {
        resetMentionState();
        return;
      }

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        resetMentionState();
        return;
      }

      const range = selection.getRangeAt(0);
      const container = editorRef.current;
      if (!container || !container.contains(range.startContainer)) {
        resetMentionState();
        return;
      }

      emojiSelectionRef.current = range.cloneRange();

      let node = range.startContainer;
      let offset = range.startOffset;

      if (node.nodeType !== Node.TEXT_NODE) {
        if (offset > 0) {
          node = node.childNodes[offset - 1];
          while (node && node.nodeType !== Node.TEXT_NODE) {
            node = node.lastChild || node.previousSibling;
          }
          if (!node) {
            resetMentionState();
            return;
          }
          offset = node.textContent.length;
        } else {
          const previousTextNode = findPreviousTextNode(node);
          if (!previousTextNode) {
            resetMentionState();
            return;
          }
          node = previousTextNode;
          offset = node.textContent.length;
        }
      }

      const textBeforeCursor = node.textContent.slice(0, offset);
      const match = MENTION_QUERY_REGEX.exec(textBeforeCursor);
      if (!match) {
        resetMentionState();
        return;
      }

      const query = (match[1] || '').replace(/\u00A0/g, ' ');
      const triggerLength = query.length + 1;
      const startOffset = offset - triggerLength;
      if (startOffset < 0) {
        resetMentionState();
        return;
      }

      const mentionRange = window.document.createRange();
      mentionRange.setStart(node, startOffset);
      mentionRange.setEnd(node, offset);
      mentionRangeRef.current = mentionRange;
      setMentionActive(true);
      setMentionQuery(query);
      if (lastMentionQueryRef.current !== query) {
        setMentionHighlightedIndex(0);
      }
      lastMentionQueryRef.current = query;
      positionMentionAnchor(mentionRange);
    }, [mentionEnabled, positionMentionAnchor]);

    const handleInput = useCallback(() => {
      const node = editorRef.current;
      if (!node) return;

      const rawHtml = node.innerHTML;
      const sanitizedHtml = sanitizeComposerHtml(rawHtml);
      if (rawHtml !== sanitizedHtml) {
        node.innerHTML = sanitizedHtml;
      }

      linkifyEditorContent();

      const linkifiedHtml = node.innerHTML;
      const finalHtml = sanitizeComposerHtml(linkifiedHtml);
      if (linkifiedHtml !== finalHtml) {
        node.innerHTML = finalHtml;
      }

      const nextPlainText = stripHtmlToPlain(finalHtml);

      if (maxLength && nextPlainText.length > maxLength) {
        const previous = lastValidStateRef.current;
        if (previous?.html !== undefined) {
          node.innerHTML = previous.html;
          setHtml(previous.html);
          setPlainText(previous.plainText || '');
          if (typeof window !== 'undefined') {
            const selection = window.getSelection();
            if (selection) {
              selection.removeAllRanges();
              const range = window.document.createRange();
              range.selectNodeContents(node);
              range.collapse(false);
              selection.addRange(range);
            }
          }
        }
        return;
      }

      setHtml(finalHtml);
      setPlainText(nextPlainText);
      lastValidStateRef.current = {
        html: finalHtml,
        plainText: nextPlainText,
      };
      updateMentionContext();
    }, [linkifyEditorContent, maxLength, updateMentionContext]);

    const handlePaste = useCallback(
      (event) => {
        if (!editorRef.current) return;
        event.preventDefault();
        const clipboard = event.clipboardData;
        const htmlData = clipboard?.getData('text/html');
        const textData = clipboard?.getData('text/plain') || '';
        const incoming = htmlData || textToHtml(textData);
        const sanitized = sanitizeComposerHtml(incoming);
        insertHtmlAtCursor(editorRef.current, sanitized);
        handleInput();
      },
      [handleInput],
    );

    const insertEmoji = useCallback(
      (emojiData) => {
        if (!emojiData) return;

        const payload =
          typeof emojiData === 'string'
            ? { emoji: emojiData }
            : emojiData && typeof emojiData === 'object'
              ? emojiData
              : null;

        if (!payload) return;

        const { emoji: emojiChar, assetUrl, key: emojiKey, label: emojiLabel } = payload;
        if (!emojiChar && !assetUrl) return;

        const editorNode = editorRef.current;
        if (!editorNode) return;

        emojiSuppressCloseRef.current = true;
        if (typeof window !== 'undefined') {
          const selection = window.getSelection();
          let storedRange = null;
          if (emojiSelectionRef.current) {
            try {
              storedRange = emojiSelectionRef.current.cloneRange();
            } catch (error) {
              storedRange = null;
            }
          }
          if (selection) {
            try {
              selection.removeAllRanges();
              if (storedRange) {
                selection.addRange(storedRange);
              } else {
                focusEditor();
              }
            } catch (error) {
              focusEditor();
            }
          }
        }

        let htmlToInsert = null;

        if (assetUrl) {
          const emojiClass = s.emojiToken || '';
          const classAttribute = emojiClass ? ` class="${escapeHtml(emojiClass)}"` : '';
          const keyAttribute = emojiKey ? ` data-emoji-key="${escapeHtml(emojiKey)}"` : '';
          const altText = emojiLabel || emojiChar || '';
          const altAttribute = ` alt="${escapeHtml(altText)}"`;
          const srcAttribute = ` src="${escapeHtml(assetUrl)}"`;
          const typeAttribute = ' data-emoji-type="custom"';
          const fallback = emojiChar || '';
          const fallbackAttribute = fallback ? ` data-emoji-fallback="${escapeHtml(fallback)}"` : '';
          htmlToInsert = `<img${classAttribute}${srcAttribute}${altAttribute}${keyAttribute}${typeAttribute}${fallbackAttribute} />`;
        } else if (emojiChar && typeof emojiChar === 'string') {
          htmlToInsert = escapeHtml(emojiChar);
        }

        if (!htmlToInsert) return;

        insertHtmlAtCursor(editorNode, htmlToInsert);
        handleInput();
        if (typeof window !== 'undefined') {
          requestAnimationFrame(() => {
            focusEditor();
            saveCurrentSelection();
            emojiSuppressCloseRef.current = false;
          });
        }
      },
      [focusEditor, handleInput, saveCurrentSelection],
    );

    const handleKeyDown = useCallback(
      (event) => {
        if (!mentionActive) return;
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          if (mentionSuggestions.length === 0) return;
          setMentionHighlightedIndex((prev) => (prev + 1) % mentionSuggestions.length);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          if (mentionSuggestions.length === 0) return;
          setMentionHighlightedIndex((prev) =>
            prev === 0 ? mentionSuggestions.length - 1 : prev - 1,
          );
        } else if (event.key === 'Enter') {
          if (mentionSuggestions.length === 0) return;
          event.preventDefault();
          const chosen = mentionSuggestions[mentionHighlightedIndex] || mentionSuggestions[0];
          if (chosen) insertMention(chosen);
        } else if (event.key === 'Escape') {
          event.preventDefault();
          setMentionActive(false);
          setMentionQuery('');
          setMentionSuggestions([]);
        }
      },
      [mentionActive, mentionHighlightedIndex, mentionSuggestions],
    );

    const handleScroll = useCallback(() => {
      if (mentionActive && mentionRangeRef.current) {
        positionMentionAnchor(mentionRangeRef.current);
      }
    }, [mentionActive, positionMentionAnchor]);

    const insertMention = useCallback(
      (mention) => {
        const range = mentionRangeRef.current;
        if (!range) return;
        range.deleteContents();
        const mentionNode = window.document.createElement('a');
        mentionNode.textContent = mention.name || mention.slug || 'member';
        mentionNode.className = mentionClassForType(mention.type);
        mentionNode.setAttribute('data-mention-type', mention.type || 'user');
        mentionNode.setAttribute('data-mention-id', mention.id || '');
        mentionNode.setAttribute('data-mention-name', mention.name || '');
        mentionNode.setAttribute('data-mention-slug', mention.slug || '');

        const resolvedHref =
          mention.href ||
          (mention.slug
            ? mention.type === 'grave'
              ? `/graves/${mention.slug}`
              : `/members/${mention.slug}`
            : '');

        if (resolvedHref) {
          mentionNode.setAttribute('href', resolvedHref);
          mentionNode.setAttribute('data-mention-href', resolvedHref);
        }

        mentionNode.setAttribute('contenteditable', 'false');
        mentionNode.setAttribute('tabindex', '-1');
        mentionNode.setAttribute('title', mention.name || mention.slug || '');

        const spaceNode = window.document.createTextNode('\u00A0');

        const fragment = range.createContextualFragment('');
        fragment.appendChild(mentionNode);
        fragment.appendChild(spaceNode);
        range.insertNode(fragment);

        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          const newRange = window.document.createRange();
          newRange.setStartAfter(spaceNode);
          newRange.collapse(true);
          selection.addRange(newRange);
        }

        setMentionActive(false);
        setMentionQuery('');
        setMentionSuggestions([]);
        setMentionHighlightedIndex(0);
        setMentionLoading(false);
        handleInput();
      },
      [handleInput],
    );

    const handleMentionClick = useCallback(
      (event, mention) => {
        event.preventDefault();
        insertMention(mention);
      },
      [insertMention],
    );

    const trimmedMentionQuery = mentionQuery.trim();

    useEffect(() => {
      if (!mentionEnabled || !fetchMentionables || !mentionActive) {
        setMentionLoading(false);
        return;
      }
      if (!trimmedMentionQuery) {
        setMentionSuggestions([]);
        setMentionLoading(false);
        return;
      }
      const requestId = mentionRequestRef.current + 1;
      mentionRequestRef.current = requestId;
      setMentionLoading(true);
      Promise.resolve(
        fetchMentionables(trimmedMentionQuery, { limit: mentionLimit, types: mentionTypesList }),
      )
        .then((items) => {
          if (mentionRequestRef.current !== requestId) return;
          setMentionSuggestions(Array.isArray(items) ? items : []);
          setMentionHighlightedIndex(0);
          setMentionLoading(false);
        })
        .catch(() => {
          if (mentionRequestRef.current !== requestId) return;
          setMentionSuggestions([]);
          setMentionLoading(false);
        });
    }, [fetchMentionables, mentionActive, mentionEnabled, mentionLimit, mentionQuery, mentionTypesList, trimmedMentionQuery]);

    useEffect(() => {
      if (mentionActive && mentionRangeRef.current) {
        positionMentionAnchor(mentionRangeRef.current);
      }
      if (!mentionActive) {
        setMentionAnchorStyle(null);
        setMentionLoading(false);
      }
    }, [mentionActive, mentionSuggestions.length, positionMentionAnchor]);

    const mentionPopoverContent = (
        mentionSuggestions.length === 0 ? (
          mentionLoading ? (
            <div className={clsx(s.suggestionItem, s.suggestionLoading)} role="status">
              <Spinner size={20} className="ng-spinner--brand" />
              <div className={s.suggestionName}>
                <FormattedMessage id="loading" defaultMessage="Loading…" />
              </div>
            </div>
          ) : (
            <div className={s.suggestionItem}>
              <div className={s.suggestionName}>
                <FormattedMessage
                  id={
                    trimmedMentionQuery
                      ? 'taggable_composer_no_matches'
                      : 'taggable_composer_type_to_tag'
                  }
                  defaultMessage={trimmedMentionQuery ? 'No matches found' : 'Type a name to tag'}
                />
              </div>
            </div>
          )
        ) : (
          mentionSuggestions.map((suggestion, index) => {
            const isActive = index === mentionHighlightedIndex;
            const displayName = suggestion.name || suggestion.slug || '';
            const initials = (() => {
              if (!displayName) return '?';
              const collected = [];
              const segments = displayName.split(/\s+/);
              for (let i = 0; i < segments.length && collected.length < 2; i += 1) {
                const match = segments[i].match(/[A-Za-z]/i);
                if (match) collected.push(match[0]);
              }
              if (collected.length === 0) {
                const letters = displayName.match(/[A-Za-z]/gi) || [];
                if (letters.length === 0) return '?';
                collected.push(...letters.slice(0, 2));
              }
              return collected.join('').toUpperCase();
            })();
            const avatarSrc = suggestion.avatarUrl ? protectedUrl(suggestion.avatarUrl) : null;
            return (
              <Button
                key={`${suggestion.type || 'user'}:${suggestion.id}`}
                type="button"
                variant="text"
                color={isActive ? 'primary' : 'text'}
                block
                className={clsx(s.suggestionItem, isActive && s.suggestionItemActive)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => handleMentionClick(event, suggestion)}
                role="option"
                aria-selected={isActive}
                leftIcon={
                  <Avatar
                    src={avatarSrc}
                    initials={initials}
                    size={30}
                    className={s.suggestionAvatar}
                  />
                }
              >
                <span className={s.suggestionName}>{displayName || 'Member'}</span>
              </Button>
            );
          })
        )
    );

    useEffect(() => {
      if (!linkPreviewEnabled) return;
      setPreviewMap((prev) => {
        const next = {};
        let changed = false;
        urls.forEach((url) => {
          const existing = prev[url];
          if (existing) {
            next[url] = existing;
          } else {
            next[url] = { status: 'loading' };
            changed = true;
          }
        });
        if (Object.keys(prev).length !== Object.keys(next).length) {
          changed = true;
        } else if (!changed) {
          for (const key of Object.keys(next)) {
            if (prev[key] !== next[key]) {
              changed = true;
              break;
            }
          }
        }
        return changed ? next : prev;
      });
    }, [linkPreviewEnabled, urls]);

    useEffect(() => {
      const timers = previewTimersRef.current;

      if (!linkPreviewEnabled || !fetchLinkPreview) {
        timers.forEach((timer) => clearTimeout(timer));
        timers.clear();
        return;
      }

      Object.entries(previewMap).forEach(([url, entry]) => {
        if (entry?.status !== 'loading') {
          if (timers.has(url)) {
            clearTimeout(timers.get(url));
            timers.delete(url);
          }
          return;
        }

        if (timers.has(url)) return;

        const timer = setTimeout(() => {
          Promise.resolve(fetchLinkPreview(url))
            .then((data) => {
              setPreviewMap((prev) => {
                const current = prev[url];
                if (!current || current.status !== 'loading') return prev;
                if (!data) {
                  return { ...prev, [url]: { status: 'error' } };
                }
                const normalizedUrl = normalizeUrl(data.url) || url;
                const normalized = {
                  url: normalizedUrl,
                  siteName: data.siteName || siteNameFromUrl(normalizedUrl),
                  title: data.title || '',
                  description: data.description || '',
                  imageUrl: data.imageUrl || '',
                };
                return { ...prev, [url]: { status: 'ready', data: normalized } };
              });
            })
            .catch(() => {
              setPreviewMap((prev) => {
                const current = prev[url];
                if (!current || current.status !== 'loading') return prev;
                return { ...prev, [url]: { status: 'error' } };
              });
            })
            .finally(() => {
              const activeTimers = previewTimersRef.current;
              if (activeTimers.has(url)) {
                clearTimeout(activeTimers.get(url));
                activeTimers.delete(url);
              }
            });
        }, LINK_PREVIEW_DEBOUNCE_MS);

        timers.set(url, timer);
      });
    }, [fetchLinkPreview, linkPreviewEnabled, previewMap]);

    useEffect(() => () => {
      const timers = previewTimersRef.current;
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    }, []);

    const readyPreviews = useMemo(
      () =>
        Object.values(previewMap)
          .filter((entry) => entry?.status === 'ready' && entry.data)
          .map((entry) => entry.data),
      [previewMap],
    );

    const hasLoadingPreview = useMemo(
      () => Object.values(previewMap).some((entry) => entry?.status === 'loading'),
      [previewMap],
    );

    const hasPreviewError = useMemo(
      () => Object.values(previewMap).some((entry) => entry?.status === 'error'),
      [previewMap],
    );

    const handleClearComposer = useCallback(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = '';
      }
      setHtml('');
      setPlainText('');
      setPreviewMap({});
      lastPayloadSignatureRef.current = null;
      if (onChange) {
        onChange({
          editorState: null,
          html: '',
          plainText: '',
          mentions: [],
          mentionsByType: {},
          urls: [],
          linkPreviews: [],
          charCount: 0,
          hasPreviewError: false,
          isFetchingPreview: false,
        });
      }
    }, [onChange]);

    const handleRemovePreview = useCallback((url) => {
      const normalizedUrl = normalizeUrl(url) || url;
      if (!normalizedUrl) return;
      setPreviewMap((prev) => {
        if (!prev) return prev;
        let key = normalizedUrl;
        if (!prev[key]) {
          const direct = prev[url];
          if (direct) {
            key = url;
          } else {
            return prev;
          }
        }
        const current = prev[key];
        if (!current || current.status === 'dismissed') return prev;
        return {
          ...prev,
          [key]: {
            ...current,
            status: 'dismissed',
          },
        };
      });
    }, []);

    const emitChange = useCallback(() => {
      if (!onChange) return;
      const mentions = collectMentionsFromContainer(editorRef.current);
      const payload = {
        editorState: null,
        html,
        plainText,
        mentions,
        mentionsByType: buildMentionsByType(mentions),
        urls,
        linkPreviews: readyPreviews,
        charCount: plainText.length,
        hasPreviewError,
        isFetchingPreview: hasLoadingPreview,
      };

      const signature = JSON.stringify({
        html: payload.html,
        text: payload.plainText,
        urls: payload.urls,
        mentions: payload.mentions.map((mention) => `${mention.type}:${mention.id}`),
        previews: payload.linkPreviews.map((preview) => preview.url),
        error: payload.hasPreviewError,
        loading: payload.isFetchingPreview,
      });

      if (lastPayloadSignatureRef.current === signature) return;
      lastPayloadSignatureRef.current = signature;
      onChange(payload);
    }, [hasLoadingPreview, hasPreviewError, html, onChange, plainText, readyPreviews, urls]);

    useEffect(() => {
      emitChange();
    }, [emitChange]);

    useEffect(() => {
      if (maxLength && plainText.length > maxLength) return;
      lastValidStateRef.current = { html, plainText };
    }, [html, plainText, maxLength]);

    const showPlaceholder = !plainText && Boolean(placeholder);
    const handleEditorMouseDown = useCallback(
      (event) => {
        if (!editorRef.current) return;
        const target = event.target;
        if (editorRef.current.contains(target)) return;
        if (target?.closest(`[data-emoji-popover="true"]`)) return;
        if (target?.closest(`.${s.emojiInlineButton}`)) return;
        event.preventDefault();
        focusEditor();
      },
      [focusEditor],
    );
    const handlePlaceholderMouseDown = useCallback(
      (event) => {
        event.preventDefault();
        focusEditor();
      },
      [focusEditor],
    );
    const mentionPopoverHostStyle = {
      position: 'absolute',
      top: mentionAnchorStyle?.top ?? 0,
      left: mentionAnchorStyle?.left ?? 0,
      width: 0,
      height: 0,
      pointerEvents: 'none',
      display: 'block',
    };

    const showCharMetrics = Boolean(showCharCount && maxLength);
    const showInlineEmojiButton = Boolean(emojiButtonEnabled);
    const shouldRenderFooter = showCharMetrics;
    const emojiButtonLabel = intl.formatMessage({
      id: 'composer_add_emoji',
      defaultMessage: 'Add emoji',
    });

    return (
      <div className={clsx(s.root, className)} style={style}>
        <div className={s.composer}>
          <div
            ref={editorContainerRef}
            className={clsx(s.editor, isFocused && s.editorFocused)}
            onMouseDown={handleEditorMouseDown}
            onClick={updateMentionContext}
          >
            {showPlaceholder ? (
              <div className={s.placeholder} onMouseDown={handlePlaceholderMouseDown}>
                {placeholder}
              </div>
            ) : null}
            <Popover
              trigger={[]}
              arrow={false}
              placement="bottomLeft"
              closeOnBlur={false}
              destroyOnHidden
              open={Boolean(mentionEnabled && mentionActive && mentionAnchorStyle)}
              content={mentionPopoverContent}
              getPopupContainer={resolveMentionContainer}
              zIndex={1600}
              style={mentionPopoverHostStyle}
            >
              <span
                ref={mentionAnchorRef}
                className={s.mentionAnchor}
              />
            </Popover>
            <div
              ref={editorRef}
              className={s.editable}
              contentEditable
              role="textbox"
              aria-label={ariaLabel}
              aria-labelledby={ariaLabelledBy}
              spellCheck
              data-placeholder={placeholder}
              onInput={handleInput}
              onPaste={handlePaste}
              onKeyDown={handleKeyDown}
              onKeyUp={updateMentionContext}
              onMouseUp={updateMentionContext}
              onScroll={handleScroll}
              onFocus={() => {
                setIsFocused(true);
                updateMentionContext();
              }}
              onBlur={() => {
                setIsFocused(false);
                setMentionActive(false);
                setMentionQuery('');
                emojiSuppressCloseRef.current = false;
                setEmojiOpen(false);
              }}
            />
            {showInlineEmojiButton ? (
              <div className={s.emojiInlineButton}>
                <Popover
                  trigger="click"
                  placement="bottom"
                  arrow={false}
                  open={emojiOpen}
                  onOpenChange={(nextOpen) => {
                    if (!nextOpen && emojiSuppressCloseRef.current) {
                      emojiSuppressCloseRef.current = false;
                      return;
                    }
                    emojiSuppressCloseRef.current = false;
                    if (nextOpen) saveCurrentSelection();
                    setEmojiOpen(nextOpen);
                  }}
                  closeOnBlur={false}
                  destroyOnHidden
                  getPopupContainer={resolveMentionContainer}
                  zIndex={1600}
                  content={
                    <div
                      data-emoji-popover="true"
                      onMouseDownCapture={() => {
                        emojiSuppressCloseRef.current = true;
                      }}
                      onTouchStartCapture={() => {
                        emojiSuppressCloseRef.current = true;
                      }}
                    >
                      <TaggableComposerEmojiPicker open={emojiOpen} onSelect={insertEmoji} />
                    </div>
                  }
                >
                  <Button
                    size="small"
                    shape="circle"
                    variant="text"
                    icon={<Smile size={16} aria-hidden />}
                    aria-label={emojiButtonLabel}
                    onClick={handleEmojiButtonClick}
                    onMouseDown={saveCurrentSelection}
                  />
                </Popover>
              </div>
            ) : null}
          </div>
        </div>
        {shouldRenderFooter ? (
          <div className={s.charRow}>
            <div className={s.charRowLeft}>
              {showCharMetrics ? (
                <Button
                  size="small"
                  variant="filled"
                  color="var(--surface-3)"
                  style={{ color: 'var(--text)' }}
                  onClick={handleClearComposer}
                  disabled={!plainText && readyPreviews.length === 0}
                >
                  <FormattedMessage id="taggable_composer_clear" defaultMessage="Clear" />
                </Button>
              ) : null}
            </div>
            <div className={s.charRowRight}>
              {showCharMetrics ? (
                <div
                  className={clsx(
                    s.charCount,
                    plainText.length > maxLength && s.charCountExceeded,
                  )}
                >
                  {plainText.length}/{maxLength}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
        {linkPreviewEnabled ? (
          <LinkPreviewList
            previews={readyPreviews}
            loading={hasLoadingPreview}
            error={hasPreviewError}
            onRemove={handleRemovePreview}
            className={s.previewList}
          />
        ) : null}
      </div>
    );
  },
);

export default TaggableComposer;
